import { CSSProperties, useEffect, useMemo, useRef } from 'react';

/**
 * WorldScene — the ETERNITY world visual that carries heroes until real key
 * art exists. Used full-bleed behind hero content on Home and /eternity.
 *
 * Contract (stable — pages depend on it):
 *   <WorldScene intensity="full" | "calm" />
 * - position: absolute, inset 0; parent must be position:relative + overflow:hidden.
 * - 'full'  = hero usage (richest version, animated atmosphere)
 * - 'calm'  = band/secondary usage (quieter, cheaper)
 * - Must respect prefers-reduced-motion (static frame, no canvas loops).
 * - Must stay legible behind light text: keep the upper third dark.
 *
 * Built like a graded plate, back to front:
 *   sky gradient → horizon glow → far skyline (hazed) → mid skyline →
 *   defocused bokeh band → near skyline (black) → drifting haze →
 *   red signal light → rain canvas → vignette → film grain.
 */

interface WorldSceneProps {
    intensity?: 'full' | 'calm';
    style?: CSSProperties;
}

/* ------------------------------------------------------------------ */
/* Deterministic generation (seeded, so the frame is stable per build)  */
/* ------------------------------------------------------------------ */

const VBW = 1600; // skyline viewBox width

function mulberry32(a: number) {
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

interface Spire {
    x: number;
    y: number;
}

interface SkylineCfg {
    minH: number;
    maxH: number;
    minW: number;
    maxW: number;
    antennaChance: number;
    gapChance: number;
}

/** Irregular city massing: flat-topped blocks, occasional low gaps,
 *  thin antenna spikes on the taller towers. Height distribution is
 *  biased low (pow) so tall towers stay rare and believable. */
function makeSkyline(seed: number, H: number, cfg: SkylineCfg): { d: string; spires: Spire[] } {
    const rnd = mulberry32(seed);
    const spires: Spire[] = [];
    const pts: string[] = [`M -24 ${H + 24}`, `L -24 ${H - cfg.minH}`];
    let x = -24;

    while (x < VBW + 24) {
        const gap = rnd() < cfg.gapChance;
        const w = (cfg.minW + rnd() * (cfg.maxW - cfg.minW)) * (gap ? 0.7 : 1);
        const h = gap
            ? cfg.minH * (0.25 + rnd() * 0.5)
            : cfg.minH + Math.pow(rnd(), 1.7) * (cfg.maxH - cfg.minH);
        const top = H - h;

        pts.push(`L ${x.toFixed(1)} ${top.toFixed(1)}`);

        // occasional setback on wide tall blocks (stepped rooflines)
        if (!gap && w > 56 && rnd() < 0.4) {
            const wStep = w * (0.3 + rnd() * 0.35);
            const stepTop = top + (6 + rnd() * 18);
            pts.push(
                `L ${(x + wStep).toFixed(1)} ${top.toFixed(1)}`,
                `L ${(x + wStep).toFixed(1)} ${stepTop.toFixed(1)}`,
                `L ${(x + w).toFixed(1)} ${stepTop.toFixed(1)}`
            );
        } else {
            // antenna spike on tall narrow towers
            if (!gap && h > cfg.maxH * 0.55 && rnd() < cfg.antennaChance) {
                const ax = x + w * (0.2 + rnd() * 0.6);
                const ah = H * 0.1 + rnd() * H * 0.16;
                pts.push(
                    `L ${(ax - 1.6).toFixed(1)} ${top.toFixed(1)}`,
                    `L ${(ax - 1.6).toFixed(1)} ${(top - ah).toFixed(1)}`,
                    `L ${(ax + 1.6).toFixed(1)} ${(top - ah).toFixed(1)}`,
                    `L ${(ax + 1.6).toFixed(1)} ${top.toFixed(1)}`
                );
                spires.push({ x: ax, y: top - ah });
            }
            pts.push(`L ${(x + w).toFixed(1)} ${top.toFixed(1)}`);
        }
        x += w;
    }

    pts.push(`L ${VBW + 24} ${H + 24}`, 'Z');
    return { d: pts.join(' '), spires };
}

interface BokehDot {
    left: number;
    bottom: number;
    size: number;
    blur: number;
    color: string;
    opacity: number;
    dur: number;
    delay: number;
}

/** Defocused city lights hugging the horizon band. Mostly cold teal/blue,
 *  a few warm sodium points. Sizes biased small; the rare big soft disc
 *  reads as a true out-of-focus highlight. */
function makeBokeh(seed: number, count: number): BokehDot[] {
    const rnd = mulberry32(seed);
    return Array.from({ length: count }, () => {
        const size = 2 + Math.pow(rnd(), 2.4) * 18;
        const pick = rnd();
        const color = pick < 0.2 ? '255,196,134' : pick < 0.62 ? '116,222,232' : '148,188,235';
        return {
            left: rnd() * 100,
            bottom: 11.5 + Math.pow(rnd(), 2.1) * 9,
            size,
            blur: 1.5 + size * 0.4,
            color,
            opacity: 0.08 + rnd() * 0.22,
            dur: 10 + rnd() * 18,
            delay: -rnd() * 24,
        };
    });
}

interface PointLight {
    left: number;
    bottom: number;
    size: number;
    color: string;
    opacity: number;
}

/** Tiny sharp distant lights along the horizon band — anchors the bokeh so
 *  the field reads as a real depth-of-field falloff, not just soft blobs. */
function makePoints(seed: number, count: number): PointLight[] {
    const rnd = mulberry32(seed);
    return Array.from({ length: count }, () => {
        const pick = rnd();
        // warm lights cluster left (sodium district), cold lights right
        const left = pick < 0.3 ? rnd() * 45 : rnd() * 100;
        const warm = pick < 0.3;
        const color = warm ? '255,198,138' : pick < 0.7 ? '140,215,225' : '170,195,230';
        return {
            left,
            bottom: 11 + Math.pow(rnd(), 2.4) * 6.5,
            size: rnd() < 0.8 ? 1 : 2,
            color,
            opacity: warm ? 0.08 + rnd() * 0.14 : 0.1 + rnd() * 0.22,
        };
    });
}

/* layer heights as % of container */
const FAR_H = 26;
const MID_H = 21;
const NEAR_H = 14;

const farLayer = makeSkyline(11, 240, {
    minH: 42, maxH: 228, minW: 13, maxW: 44, antennaChance: 0.5, gapChance: 0.2,
});
const midLayer = makeSkyline(47, 220, {
    minH: 30, maxH: 188, minW: 20, maxW: 62, antennaChance: 0.45, gapChance: 0.26,
});
const nearLayer = makeSkyline(83, 200, {
    minH: 18, maxH: 152, minW: 34, maxW: 108, antennaChance: 0.55, gapChance: 0.34,
});

/* red signal light: tallest spire of the near layer */
const signalSpire = nearLayer.spires.reduce(
    (best, s) => (s.y < best.y ? s : best),
    nearLayer.spires[0] ?? { x: VBW * 0.62, y: 40 }
);
const SIGNAL_LEFT = (signalSpire.x / VBW) * 100;
const SIGNAL_BOTTOM = NEAR_H * ((200 - signalSpire.y) / 200);

const GRAIN_URI =
    "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/* ------------------------------------------------------------------ */
/* Rain                                                                 */
/* ------------------------------------------------------------------ */

interface Drop {
    x: number;
    y: number;
    z: number; // 0 far .. 1 near
    speed: number;
    len: number;
    alpha: number;
}

function useRain(canvasRef: React.RefObject<HTMLCanvasElement>, enabled: boolean) {
    useEffect(() => {
        if (!enabled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        let raf = 0;
        let running = false;
        let w = 0;
        let h = 0;
        let drops: Drop[] = [];
        let last = 0;

        const rnd = Math.random;

        const makeDrop = (anywhere: boolean): Drop => {
            const z = Math.pow(rnd(), 1.4);
            return {
                x: rnd() * (w + 60) - 30,
                y: anywhere ? rnd() * h : -30 - rnd() * 80,
                z,
                speed: 340 + z * 420,
                len: 8 + z * 15,
                alpha: 0.03 + z * 0.09,
            };
        };

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            w = rect.width;
            h = rect.height;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.max(1, Math.round(w * dpr));
            canvas.height = Math.max(1, Math.round(h * dpr));
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.min(130, Math.max(36, Math.round(w / 12)));
            drops = Array.from({ length: count }, () => makeDrop(true));
        };

        const SLANT = 0.14; // rain angle: dx per dy

        const tick = (t: number) => {
            if (!running) return;
            const dt = Math.min(0.05, (t - last) / 1000 || 0.016);
            last = t;
            ctx.clearRect(0, 0, w, h);
            ctx.lineCap = 'round';
            for (const d of drops) {
                d.y += d.speed * dt;
                d.x += d.speed * SLANT * dt;
                if (d.y - d.len > h) {
                    const nd = makeDrop(false);
                    d.x = nd.x; d.y = nd.y; d.z = nd.z;
                    d.speed = nd.speed; d.len = nd.len; d.alpha = nd.alpha;
                }
                // rain reads denser/heavier near the ground
                const depthFade = 0.35 + 0.65 * Math.min(1, Math.max(0, d.y / h));
                ctx.strokeStyle = `rgba(168, 198, 222, ${(d.alpha * depthFade).toFixed(3)})`;
                ctx.lineWidth = 0.5 + d.z * 0.7;
                ctx.beginPath();
                ctx.moveTo(d.x, d.y);
                ctx.lineTo(d.x - d.len * SLANT, d.y - d.len);
                ctx.stroke();
            }
            raf = requestAnimationFrame(tick);
        };

        const start = () => {
            if (running || mq.matches) return;
            running = true;
            last = performance.now();
            raf = requestAnimationFrame(tick);
        };
        const stop = () => {
            running = false;
            cancelAnimationFrame(raf);
            ctx.clearRect(0, 0, w, h);
        };
        const onMotionPref = () => (mq.matches ? stop() : start());

        resize();
        start();
        window.addEventListener('resize', resize);
        mq.addEventListener('change', onMotionPref);

        return () => {
            stop();
            window.removeEventListener('resize', resize);
            mq.removeEventListener('change', onMotionPref);
        };
    }, [canvasRef, enabled]);
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

const WorldScene = ({ intensity = 'full', style }: WorldSceneProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const full = intensity === 'full';
    useRain(canvasRef, full);

    const bokeh = useMemo(() => makeBokeh(7, full ? 42 : 18), [full]);
    const points = useMemo(() => makePoints(29, full ? 54 : 24), [full]);

    return (
        <div className={`world-scene world-scene-${intensity}`} style={style} aria-hidden="true">
            <style>{`
                .world-scene {
                    position: absolute;
                    inset: 0;
                    overflow: hidden;
                    pointer-events: none;
                    background: linear-gradient(180deg,
                        #020309 0%,
                        #04050c 38%,
                        #060914 64%,
                        #0a1020 88%,
                        #0c1322 100%);
                }
                .ws-fill { position: absolute; inset: 0; }

                /* faint tonal modulation so the upper sky isn't a dead gradient */
                .ws-skymod {
                    background:
                        radial-gradient(60% 38% at 76% 16%, rgba(38, 58, 98, 0.07) 0%, transparent 70%),
                        radial-gradient(50% 30% at 18% 34%, rgba(30, 46, 84, 0.05) 0%, transparent 70%);
                }

                /* cold ambient city glow rising from the horizon */
                .ws-glow {
                    background:
                        radial-gradient(95% 27% at 52% 100%, rgba(86, 178, 196, 0.28) 0%, rgba(48, 106, 142, 0.13) 48%, transparent 74%),
                        radial-gradient(110% 48% at 50% 104%, rgba(44, 102, 134, 0.16) 0%, transparent 70%),
                        radial-gradient(34% 15% at 71% 100%, rgba(120, 225, 235, 0.16) 0%, transparent 70%),
                        radial-gradient(44% 17% at 20% 100%, rgba(222, 146, 82, 0.17) 0%, rgba(180, 110, 70, 0.07) 55%, transparent 75%);
                }

                .ws-skyline {
                    position: absolute;
                    bottom: 0; left: 0;
                    width: 100%;
                    display: block;
                }
                .ws-sky-far  { height: ${FAR_H}%;  filter: blur(1.7px); }
                .ws-sky-mid  { height: ${MID_H}%;  filter: blur(0.6px); }
                .ws-sky-near { height: ${NEAR_H}%; }

                /* drifting volumetric haze — huge soft gradients, transform-only */
                .ws-haze {
                    position: absolute;
                    left: -32%; width: 164%;
                    will-change: transform;
                }
                .ws-haze-a {
                    bottom: 4%; height: 44%;
                    background: radial-gradient(58% 60% at 36% 72%, rgba(66, 118, 148, 0.10), transparent 72%);
                    animation: ws-drift-a 140s ease-in-out infinite alternate;
                }
                .ws-haze-b {
                    bottom: 0; height: 30%;
                    background: radial-gradient(52% 70% at 64% 86%, rgba(96, 170, 188, 0.09), transparent 70%);
                    animation: ws-drift-b 100s ease-in-out infinite alternate;
                }
                .ws-haze-c {
                    bottom: 0; height: 22%;
                    background: linear-gradient(180deg, transparent, rgba(110, 160, 185, 0.07) 60%, rgba(120, 170, 195, 0.10));
                    animation: ws-drift-c 170s ease-in-out infinite alternate;
                }
                @keyframes ws-drift-a { from { transform: translate3d(-2.6%, 0, 0); } to { transform: translate3d(2.6%, 0, 0); } }
                @keyframes ws-drift-b { from { transform: translate3d(2.2%, 0, 0); }  to { transform: translate3d(-2.2%, 0, 0); } }
                @keyframes ws-drift-c { from { transform: translate3d(-1.4%, 0, 0); } to { transform: translate3d(1.4%, 0, 0); } }

                .ws-point {
                    position: absolute;
                    border-radius: 50%;
                }

                /* defocused light bokeh near the horizon */
                .ws-bokeh-dot {
                    position: absolute;
                    border-radius: 50%;
                    will-change: opacity;
                    animation: ws-shimmer var(--dur) ease-in-out var(--delay) infinite;
                }
                @keyframes ws-shimmer {
                    0%, 100% { opacity: var(--o); }
                    50%      { opacity: calc(var(--o) * 0.5); }
                }

                /* the one red signal light */
                .ws-signal {
                    position: absolute;
                    width: 3px; height: 3px;
                    margin-left: -1.5px;
                    border-radius: 50%;
                    background: #ff5648;
                    box-shadow:
                        0 0 5px 1px rgba(255, 70, 56, 0.55),
                        0 0 13px 4px rgba(255, 48, 40, 0.16);
                    animation: ws-signal 6.4s ease-in-out infinite;
                }
                @keyframes ws-signal {
                    0%, 100% { opacity: 0.5; }
                    10%      { opacity: 1; }
                    22%      { opacity: 0.6; }
                }

                .ws-rain {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                }

                /* legibility + film edges */
                .ws-vignette {
                    background:
                        linear-gradient(180deg, rgba(1, 2, 7, 0.62) 0%, rgba(1, 2, 7, 0.22) 28%, transparent 52%),
                        radial-gradient(125% 95% at 50% 42%, transparent 52%, rgba(0, 1, 5, 0.52) 100%);
                }
                .ws-grain {
                    background-image: ${GRAIN_URI};
                    background-size: 180px 180px;
                    opacity: 0.04;
                    mix-blend-mode: screen;
                }

                .world-scene-calm .ws-haze-b,
                .world-scene-calm .ws-haze-c { display: none; }

                @media (prefers-reduced-motion: reduce) {
                    .world-scene, .world-scene * { animation: none !important; }
                    .ws-signal { opacity: 0.85; }
                }
            `}</style>

            {/* sky modulation + horizon glow */}
            <div className="ws-fill ws-skymod" />
            <div className="ws-fill ws-glow" />

            {/* haze behind the city */}
            <div className="ws-haze ws-haze-a" />

            {/* far skyline — hazed into the sky, lit from behind at street level */}
            <svg className="ws-skyline ws-sky-far" viewBox="0 0 1600 240" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="ws-g-far" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(16, 28, 52, 0.42)" />
                        <stop offset="70%" stopColor="rgba(22, 40, 68, 0.62)" />
                        <stop offset="100%" stopColor="rgba(34, 62, 92, 0.72)" />
                    </linearGradient>
                </defs>
                <path d={farLayer.d} fill="url(#ws-g-far)" />
            </svg>

            {/* mid skyline */}
            <svg className="ws-skyline ws-sky-mid" viewBox="0 0 1600 220" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="ws-g-mid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(9, 16, 31, 0.88)" />
                        <stop offset="100%" stopColor="rgba(16, 30, 52, 0.92)" />
                    </linearGradient>
                </defs>
                <path d={midLayer.d} fill="url(#ws-g-mid)" />
            </svg>

            {/* sharp distant point lights */}
            {points.map((p, i) => (
                <span
                    key={`p${i}`}
                    className="ws-point"
                    style={{
                        left: `${p.left}%`,
                        bottom: `${p.bottom}%`,
                        width: p.size,
                        height: p.size,
                        background: `rgba(${p.color}, 1)`,
                        opacity: p.opacity,
                    }}
                />
            ))}

            {/* defocused city lights */}
            {bokeh.map((b, i) => (
                <span
                    key={i}
                    className="ws-bokeh-dot"
                    style={{
                        left: `${b.left}%`,
                        bottom: `${b.bottom}%`,
                        width: b.size,
                        height: b.size,
                        background: `rgba(${b.color}, 0.9)`,
                        filter: `blur(${b.blur}px)`,
                        ['--o' as string]: b.opacity,
                        ['--dur' as string]: `${b.dur}s`,
                        ['--delay' as string]: `${b.delay}s`,
                        opacity: b.opacity,
                    }}
                />
            ))}

            {/* near skyline — almost black */}
            <svg className="ws-skyline ws-sky-near" viewBox="0 0 1600 200" preserveAspectRatio="none">
                <path d={nearLayer.d} fill="#04060d" />
            </svg>

            {/* haze in front of the city */}
            <div className="ws-haze ws-haze-b" />
            <div className="ws-haze ws-haze-c" />

            {/* red signal light on the tallest near spire */}
            <span
                className="ws-signal"
                style={{ left: `${SIGNAL_LEFT}%`, bottom: `${SIGNAL_BOTTOM}%` }}
            />

            {/* rain */}
            {full && <canvas ref={canvasRef} className="ws-rain" />}

            {/* grade: vignette + grain */}
            <div className="ws-fill ws-vignette" />
            <div className="ws-fill ws-grain" />
        </div>
    );
};

export default WorldScene;
